import { DEFAULT_CURRENCY } from '@app/common/constants';
import Text from '@app/components/ui/Text/Text';
import GlassCard from '@app/features/billing/components/BaseGlassCard/GlassCard';
import { APP_PREFIX, CUSTOMER_PROFILE_PAGE } from '@app/routes/urls';
import { currencyFormatter, type SupportedCurrencies } from '@app/utils/pricingCurrencyUtils';
import { Button, Grid, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { FiExternalLink } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import CardHeader from './CardHeader';
import { commonTableClassName } from './tableStyles';
import type { ClientDebtRow } from './types';

type ClientsWithDebtsCardProps = {
  data: ClientDebtRow[];
  totalDebt: number;
  currency?: SupportedCurrencies;
  locale: string;
  isLoading?: boolean;
  onViewAllClick?: () => void;
  className?: string;
};

const ClientsWithDebtsCard = ({
  data,
  totalDebt,
  currency = DEFAULT_CURRENCY,
  locale,
  isLoading = false,
  onViewAllClick,
  className = 'col-span-12 lg:col-span-6 p-0',
}: ClientsWithDebtsCardProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const shouldShowNameTooltip = Boolean(screens.xxl);

  const handleProfileClick = (customerId: string | number) => {
    navigate(`/${APP_PREFIX}/${CUSTOMER_PROFILE_PAGE}?id=${customerId}&type=client`);
  };

  const columns: ColumnsType<ClientDebtRow> = [
    {
      title: <Text type="label2" transKey="client-capital" fw={700} noWrap />,
      dataIndex: 'clientName',
      key: 'clientName',
      render: (value: string) => {
        const nameCell = (
          <div className="min-w-0 max-w-full">
            <Text type="body2" untranslatedText={value} noWrap isTruncated className="block" />
          </div>
        );

        if (!shouldShowNameTooltip) {
          return nameCell;
        }

        return <Tooltip title={value}>{nameCell}</Tooltip>;
      },
    },
    {
      title: <Text type="label2" transKey="balance-capital" fw={700} noWrap />,
      dataIndex: 'balance',
      key: 'balance',
      align: 'right',
      width: 110,
      render: (value: number) => (
        <Text type="body2" untranslatedText={currencyFormatter(Math.abs(value), currency, locale)} color="#0f172a" />
      ),
    },
    {
      title: <Text type="label2" transKey="last-payment-capital" fw={700} noWrap />,
      dataIndex: 'lastPaymentDate',
      key: 'lastPaymentDate',
      align: 'center',
      // Wide enough for DD/MM/YYYY once the cell's 32px of padding is taken off: at
      // 88px, sized when the date was DD/MM, the year broke onto a second line as
      // "29/07/2" / "026", and at 110px it left 78px for a string needing 80.
      width: 124,
      render: (value: string) => <Text type="body2" untranslatedText={value} color="#64748b" noWrap />,
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      width: 52,
      render: (_, record) => (
        <Tooltip title={t('ledgers')}>
          <Button
            size="small"
            type="text"
            className="h-8 w-8 p-0 text-slate-500 hover:!text-slate-700 !bg-transparent !shadow-none"
            onClick={() => handleProfileClick(record.customerId)}
            aria-label={t('ledgers')}
          >
            <FiExternalLink size={16} />
          </Button>
        </Tooltip>
      ),
    },
  ];

  const tableData = data.slice(0, 5);

  return (
    <GlassCard
      theme="balance"
      className={className}
      isLoading={isLoading}
      title={<CardHeader title={t('clients-owing-you')} onViewAllClick={onViewAllClick} />}
      titleClassName="w-full"
      bodyClassName="px-0 pb-5"
      body={
        <div className="text-base font-normal">
          <div className={commonTableClassName}>
            <Table<ClientDebtRow>
              columns={columns}
              dataSource={tableData}
              pagination={false}
              size="small"
              tableLayout="fixed"
              footer={() => (
                <div className="flex items-center justify-between border-t border-slate-200 px-3 py-3 sm:px-4">
                  <Text type="label1" transKey="total" color="#0f172a" />

                  <Text
                    type="label1"
                    untranslatedText={currencyFormatter(Math.abs(totalDebt), currency, locale)}
                    color="#0f172a"
                  />
                </div>
              )}
            />
          </div>
        </div>
      }
    />
  );
};

export default ClientsWithDebtsCard;
